import { useState, useRef, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * AddressAutocomplete — French address autocomplete using the free
 * Base Adresse Nationale (BAN) API from data.gouv.fr
 *
 * Props:
 *  - value: { street, postal_code, city, country }
 *  - onChange: (address) => void
 *  - disabled: boolean
 */
export const AddressAutocomplete = ({ value = {}, onChange, disabled = false }) => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Sync query from external value (initial load)
  useEffect(() => {
    if (value?.street && !query) {
      const parts = [value.street, value.postal_code, value.city].filter(Boolean);
      setQuery(parts.join(', '));
    }
  }, [value?.street]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = useCallback(async (q) => {
    if (!q || q.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5&autocomplete=1`
      );
      const data = await res.json();
      if (data?.features) {
        setSuggestions(
          data.features.map((f) => ({
            label: f.properties.label,
            street: f.properties.name,
            postal_code: f.properties.postcode,
            city: f.properties.city,
            context: f.properties.context,
          }))
        );
        setOpen(true);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    // Debounce 300ms
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAddress(val), 300);
  };

  const handleSelect = (suggestion) => {
    setQuery(suggestion.label);
    setOpen(false);
    setSuggestions([]);
    onChange({
      street: suggestion.street,
      postal_code: suggestion.postal_code,
      city: suggestion.city,
      country: 'France',
    });
  };

  return (
    <div ref={containerRef} className="relative">
      <Label>Adresse</Label>
      <div className="relative">
        <MapPin
          className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4"
          style={{ color: 'var(--text-muted)' }}
        />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setOpen(true)}
          placeholder="Rechercher une adresse..."
          className="pl-9"
          disabled={disabled}
          data-testid="address-autocomplete-input"
        />
        {loading && (
          <Loader2
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin"
            style={{ color: 'var(--text-muted)' }}
          />
        )}
      </div>

      {/* Dropdown suggestions */}
      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-lg border shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderColor: 'var(--neutral-200)',
          }}
          data-testid="address-suggestions"
        >
          {suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(s)}
              className={cn(
                "w-full text-left px-3 py-2.5 text-sm flex items-start gap-2",
                "transition-colors duration-150 cursor-pointer",
                "hover:bg-[var(--primary-50)]",
              )}
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: 'var(--primary-400)' }} />
              <div>
                <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {s.street}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {s.postal_code} {s.city} — {s.context}
                </p>
              </div>
            </button>
          ))}
          <div
            className="px-3 py-1.5 text-[10px] text-center border-t"
            style={{ color: 'var(--text-muted)', borderColor: 'var(--neutral-100)' }}
          >
            Données : Base Adresse Nationale (BAN)
          </div>
        </div>
      )}
    </div>
  );
};
